import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { ArrowDown, ArrowUp, CornerDownLeft, Search } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useRef, useState } from 'react';
import { Input } from 'semantic-ui-react';

import Modal from '@components/modal';
import { useNavigate } from '@hooks/use-navigate';

import type {
  CommandPaletteOutcome,
  CommandPaletteScopeDefinition,
} from './command-palette.models';
import { getOptionId } from './command-palette.utils';
// eslint-disable-next-line postcss-modules/no-unused-class -- the shell and surface share this stylesheet
import s from './command-palette.scss';

export interface CommandPaletteSurfaceProps {
  close: () => void;
  initialQuery: string;
  isOpen: boolean;
  listboxId: string;
  scope: CommandPaletteScopeDefinition;
}

/**
 * The palette body for a single active scope: the input, keyboard navigation,
 * footer hints, outcome dispatch, and the scope's rendered content.
 */
export const CommandPaletteSurface = ({
  close,
  initialQuery,
  isOpen,
  listboxId,
  scope: definition,
}: CommandPaletteSurfaceProps) => {
  useStyles(s);
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [activeIndex, setActiveIndex] = useState(0);

  // Semantic's mount transition breaks HTML autofocus; focus via the ref once
  // per open so re-renders can't steal it back.
  const hasAutoFocused = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const activate = (outcome: CommandPaletteOutcome) => {
    if (outcome.kind === 'navigate') {
      navigate(outcome.to);
      close();
      return;
    }

    outcome.run();
  };

  const scope = definition.useScope({
    activate,
    activeIndex,
    close,
    listboxId,
    onActivate: setActiveIndex,
    onSettle: () => inputRef.current?.focus(),
    query,
  });

  const submit = () => {
    setActiveIndex(0);
    scope.onSubmit();
    // A click on the action button moves focus to it; restore it so arrow-key
    // navigation keeps working. Async scopes also settle focus via onSettle.
    inputRef.current?.focus();
  };

  const moveActiveOption = (delta: number) => {
    if (scope.optionCount === 0) return;

    const next = (activeIndex + delta + scope.optionCount) % scope.optionCount;
    setActiveIndex(next);

    // Focus stays on the input, so scroll the active option into view ourselves.
    // Smooth so the scroll composes with the highlight slide instead of snapping.
    document
      .getElementById(getOptionId(listboxId, next))
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleInputKeyDown = (event: KeyboardEvent) => {
    if (
      scope.showOptions &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    ) {
      event.preventDefault();
      moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (event.key !== 'Enter') return;

    if (scope.showOptions) {
      event.preventDefault();
      const outcome = scope.outcomeAt(activeIndex);
      if (outcome) activate(outcome);
      return;
    }

    submit();
  };

  return (
    <Modal.Content className={s.content}>
      <Input
        ref={input => {
          inputRef.current = input;
          if (isOpen && input && !hasAutoFocused.current) {
            hasAutoFocused.current = true;
            input.focus();
          }
        }}
        action={{
          'aria-label': 'Search',
          className: classNames(s.searchAction, 'icon', 'darkreader-ignore', {
            [s.searchActionFilled]: !!query,
          }),
          content: <Search className={s.searchActionIcon} />,
          onClick: submit,
        }}
        aria-activedescendant={
          scope.showOptions ? getOptionId(listboxId, activeIndex) : undefined
        }
        aria-controls={scope.showOptions ? listboxId : undefined}
        aria-expanded={scope.showOptions}
        aria-label={scope.inputLabel}
        className={s.searchBar}
        fluid
        label={{ className: s.scopeChip, content: definition.label }}
        onChange={(_event, { value }) => setQuery(value)}
        onKeyDown={handleInputKeyDown}
        placeholder={scope.placeholder}
        role="combobox"
        value={query}
      />

      {scope.content}

      <div className={s.footer}>
        <span className={s.hint}>
          <kbd className={s.key}>esc</kbd>
          <span>to close</span>
        </span>

        {scope.showOptions && (
          <>
            <span className={s.hint}>
              <kbd className={s.key}>
                <ArrowDown className={s.keyIcon} />
              </kbd>
              <kbd className={s.key}>
                <ArrowUp className={s.keyIcon} />
              </kbd>
              <span>to navigate</span>
            </span>

            <span className={s.hint}>
              <kbd className={s.key}>
                <CornerDownLeft className={s.keyIcon} />
              </kbd>
              <span>to open</span>
            </span>
          </>
        )}
      </div>
    </Modal.Content>
  );
};

export default CommandPaletteSurface;
